import { Body3, Button, Price, Spinner } from '@common/components';
import { BasketItemRemoveButton } from '@common/components/BasketItem/BasketItem.styles';
import { BlockInputField } from '@common/components/FormElements';
import { useUuid } from '@common/hooks/useUuid';
import { defaultColors } from '@common/theme/colours';
import { useTranslations } from '@lang';
import { CancelCircleIcon } from '@root/styles/icons';
import { UseMutateFunction } from '@tanstack/react-query';
import React, { useState } from 'react';
import { BasketDetails } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import styled, { css } from 'styled-components';

export type OwnProps = {
  /**
   * Component only shows the name of the discount which has been applied
   */
  displayOnly?: boolean;
  /**
   * Component only shows applied discount name + removal cross
   */
  removalOnly?: boolean;
  /**
   * Show a spinner while submitting
   */
  showSpinnerOnSubmit?: boolean;
  /**
   * Type of discount (promo code or voucher)
   */
  discountType?: 'promoCode' | 'voucher';
  /**
   * Function to call on form submit
   */
  applyDiscount?: UseMutateFunction<void, unknown, string, unknown>;
  /**
   * Function to remove discount
   */
  removeDiscount?: UseMutateFunction<void, unknown, string, unknown>;
  error?: string;
  isLoading?: boolean;
  index?: number;
};

export type ReduxProps = {
  basketDetails: BasketDetails;
  property: Property;
  isFetchingBasketDetails?: boolean;
};

/**
 * @abstract
 */
export const DiscountsFormBase: React.FC<ReduxProps & OwnProps> = ({
  applyDiscount,
  removeDiscount,
  isLoading,
  basketDetails,
  displayOnly = false,
  removalOnly = false,
  showSpinnerOnSubmit = false,
  discountType = 'promoCode',
  error,
  index = 0,
}) => {
  const { t } = useTranslations();
  const appliedDiscount =
    discountType === 'promoCode'
      ? basketDetails?.activePromo
      : basketDetails?.vouchers?.[index] && {
          name: basketDetails?.vouchers[index].code,
          discountValue: basketDetails?.vouchers[index].redemptionTotal.amount,
        };

  const basketTotal = basketDetails?.total?.amount || 0;

  const [discountCode, setDiscountCode] = useState(appliedDiscount?.name ?? '');
  const id = useUuid();

  const handleSubmit = async (e: React.FormEvent, newDiscountCode: string) => {
    e.preventDefault();

    if (displayOnly) return;

    applyDiscount && applyDiscount(newDiscountCode);

    if (discountCode != newDiscountCode && discountType === 'promoCode') {
      setDiscountCode(newDiscountCode);
    } else {
      setDiscountCode('');
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    removeDiscount && removeDiscount(discountCode);
  };

  const renderDiscountAppliedBlock = () => {
    if (discountType !== 'promoCode' || !appliedDiscount) return null;

    return (
      <DiscountAppliedBlockWrapper
        as={displayOnly ? 'div' : 'button'}
        type={displayOnly ? undefined : 'button'}
        onClick={
          displayOnly
            ? undefined
            : (e: React.FormEvent) => {
                handleSubmit(e, '');
              }
        }
        displayOnly={displayOnly}
      >
        <Body3 strong>
          <div>
            {appliedDiscount.name} {t('basket.codeApplied')}
          </div>
        </Body3>
        {!displayOnly ? (
          <div>
            <RemovalCross width={22} height={22} aria-label={t('basket.removePromoCode')} />
          </div>
        ) : null}
      </DiscountAppliedBlockWrapper>
    );
  };

  const hasTableItems = () =>
    basketDetails?.items?.some((item) => item.type === 'tables' && !item.dynamicPackage) ?? false;

  const isVoucherDisabled = () => {
    if (discountType !== 'voucher') return false;

    if (!!appliedDiscount?.discountValue) return true;

    if (hasTableItems()) return false;

    return basketTotal <= 0;
  };

  const isPromoCodeDisabled = () => {
    if (discountType !== 'promoCode') return false;
    return basketTotal <= 0;
  };

  const isInputDisabled = () => {
    return discountType === 'voucher' ? isVoucherDisabled() : isPromoCodeDisabled();
  };

  const renderDiscountInputRow = () => {
    if (discountType === 'promoCode' && (displayOnly || removalOnly)) return null;
    return (
      <FormRow>
        <FormCol grow>
          <BlockInputField
            data-testid="input_discountCode"
            name="discountCode"
            id={id}
            value={discountCode}
            className={!discountCode && isInputDisabled() ? 'disabled' : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDiscountCode(e.target.value.toUpperCase())
            }
            label={
              discountType === 'promoCode' ? t('basket.promotionalCode') : t('basket.voucherCode')
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.code === 'Enter') {
                handleSubmit(e, discountCode);
              }
            }}
            disabled={isInputDisabled()}
          />
        </FormCol>
        {discountType === 'voucher' &&
          discountCode &&
          appliedDiscount?.discountValue !== undefined && (
            <FormCol>
              <DiscountAppliedWrapper>
                {!displayOnly && (
                  <BasketItemRemoveButton href="#" onClick={handleRemoveClick}>
                    {t('common.remove')}
                  </BasketItemRemoveButton>
                )}

                <Price
                  price={{
                    amount: -Number(appliedDiscount.discountValue),
                    currencyCode: basketDetails.currencyCode,
                  }}
                />
              </DiscountAppliedWrapper>
            </FormCol>
          )}
        {(discountType === 'promoCode' ||
          (discountType === 'voucher' && appliedDiscount?.discountValue === undefined)) && (
          <FormCol>
            <Button
              onClick={(e) => handleSubmit(e, discountCode)}
              isInline
              type="button"
              disabled={isLoading || !discountCode}
            >
              {t('basket.apply')}
            </Button>
          </FormCol>
        )}
      </FormRow>
    );
  };

  const renderErrorText = () => {
    if (!error) return null;

    return (
      <ErrorTextWrapper>
        <Body3 color={defaultColors.error}>{error}</Body3>
      </ErrorTextWrapper>
    );
  };

  return (
    <Container>
      <Spinner isActive={showSpinnerOnSubmit && isLoading} />
      {renderDiscountAppliedBlock()}
      {renderDiscountInputRow()}
      {renderErrorText()}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  input.disabled {
    cursor: not-allowed;
  }
`;

const FormCol = styled.div<{ grow?: boolean }>`
  display: flex;
  align-items: flex-end;

  ${({ grow }) =>
    grow &&
    css`
      flex-grow: 1;
      margin-right: 8px;
    `}
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DiscountAppliedBlockWrapper = styled.button<{ displayOnly: boolean }>`
  display: flex;
  flex-direction: row;
  border: none;
  ${(p) =>
    p.displayOnly
      ? css`
          cursor: initial;
          justify-content: center;
        `
      : css`
          cursor: pointer;
          justify-content: space-between;
        `}
  background-color: ${({ theme }) => theme.greyColors['grey-10']};
  padding: ${({ theme }) => `${theme.spaces.space2} ${theme.spaces.space3}`};
  border-radius: ${({ theme }) => theme.spaces.space1};
  margin-bottom: ${({ theme }) => theme.spaces.space5};
  align-items: center;
  flex-grow: 1;
`;

const RemovalCross = styled(CancelCircleIcon)`
  color: ${({ theme }) => theme.greyColors['grey-40']};
`;

const DiscountAppliedWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spaces.space2} ${theme.spaces.space3}`};
  width: 100%;
  gap: ${({ theme }) => theme.spaces.space3};
  white-space: nowrap;
`;

const ErrorTextWrapper = styled.div`
  margin-top: 5px;
`;
